import React, { useEffect, useState } from "react";
import ImagerApi from "../../api/ImagerApi";
import { ELightPath } from "../../Model/CaptureProfile";
import { EModuleType } from "../../Model/EModuleType";
import {
  getModule,
  getModuleById,
  getModules,
} from "../../redux/reducers/ModuleReducer";
import { getModuleState } from "../../redux/reducers/ModuleStateReducer";
import {
  getPlateAddresses,
  getPlateAddressesOnModule,
} from "../../redux/reducers/PlateAddressReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import ImagerUtil from "../../Utility/ImagerUtil";
import PlateUtil from "../../Utility/PlateUtil";
import Disabled from "../Controls/Disabled";
import AccordionToggle from "../Plate/AccordionToggle";
import WellControl from "../Plate/SideControlBar/WellControl";
import Collapse from "./../Common/Collapse";
import ImageLeveling from "./ImageLeveling";
import LiveImagePreferences from "./LiveImagePreferences";
import OpticAndCameraControl from "./OpticAndCameraControl";
import CommonDropDown from "../Common/DropDown/CommonDropDown";

const WELL = 0;
const OPTIC = 1;
const LEVELING = 2;
const INFO = 3;
const PREFERENCES = 4;
export const IMAGER_MANUAL_ACCORDION_STATE = "IMAGER_MANUAL_ACCORDION_STATE";

function getAccordionState(key: string): boolean[] {
  const value = localStorage.getItem(key);
  if (value != null) {
    return JSON.parse(value);
  } else {
    return [false, false, false, false, false];
  }
}

function getExpandAllAccordionState(key: string): boolean {
  const value = localStorage.getItem(key);
  if (value != null) {
    const accordionState: boolean[] = JSON.parse(value);
    return accordionState.some((val) => val === false);
  } else {
    return true;
  }
}

type Props = {
  imagerIdentifier: string;
  style?: React.CSSProperties;
  wellLimitEnabled: boolean;
  headerEnabled?: boolean;
  disabledLightPaths?: ELightPath[];
  triggerExpandAll?: boolean; // use boolean state, true = trigger expand/collaps all
  setTriggerExpandAll?: (triiger: boolean) => void; // update the trigger boolean state
  onAllItemCollapsed?: (collapsed: boolean) => void; // called when all accordion collapsed or not
};

export default function ImagerManualControl({
  imagerIdentifier,
  wellLimitEnabled,
  style = {},
  headerEnabled = true,
  disabledLightPaths = [],
  triggerExpandAll,
  setTriggerExpandAll = () => {},
  onAllItemCollapsed = () => {},
}: Props) {
  const [canExpandAll, setCanExpandAll] = useState(
    getExpandAllAccordionState(IMAGER_MANUAL_ACCORDION_STATE)
  );
  const [accordionState, setAccordionState] = useState(
    getAccordionState(IMAGER_MANUAL_ACCORDION_STATE)
  );
  const imager = useTypedSelector((state) =>
    getModule(state, imagerIdentifier)
  );
  const psrModules = useTypedSelector((state) =>
    getModules(state, EModuleType.PSR)
  );
  const imagerState = useTypedSelector(
    (state) => getModuleState(state, imagerIdentifier).imager
  );
  const suiteState = useTypedSelector(
    (state) => getModuleState(state, "Suite").suite
  );
  const psrState = useTypedSelector(
    (state) => getModuleState(state, psrModules[0].identifier).psr
  );
  const plateAddresses = useTypedSelector((state) => getPlateAddresses(state));
  const moduleById = useTypedSelector((state) => getModuleById(state));
  const imagerModules = useTypedSelector((state) =>
    getModules(state, EModuleType.IMAGER)
  );
  const plateTypeOnImager = useTypedSelector((state) =>
    getPlateAddressesOnModule(state, imagerModules[0].identifier)
  );

  if (imagerState == null) {
    throw new Error("Imager state is null");
  }
  if (suiteState == null) {
    throw new Error("Suite state is null");
  }
  if (psrState == null) {
    throw new Error("Psr state is null");
  }
  useEffect(() => {
    localStorage.setItem(
      IMAGER_MANUAL_ACCORDION_STATE,
      JSON.stringify(accordionState)
    );
  }, [accordionState]);

  useEffect(() => {
    onAllItemCollapsed(canExpandAll);
  }, [canExpandAll]);

  useEffect(() => {
    if (triggerExpandAll) {
      onExpandAllToggleClicked();
      setTriggerExpandAll(false);
    }
  }, [triggerExpandAll]);

  const plateExistOnImager = !!plateTypeOnImager[0].usedByPlate;

  const disableWellControl =
    !plateExistOnImager ||
    ImagerUtil.getIsWellControlDisabled(imagerState, suiteState);
  const disableOpticControl =
    !plateExistOnImager ||
    ImagerUtil.getIsOpticControlDisabled(imagerState, suiteState);
  const disableAdditionalOpticControl =
    !plateExistOnImager ||
    ImagerUtil.getIsAdditionalOpticControlDisabled(imagerState, suiteState);

  const onDropNumberChanged = (value: number) => {
    ImagerApi.moveToWell(imager.identifier, imagerState.wellNumber, value);
  };

  const onExpandAllToggleClicked = () => {
    if (!canExpandAll) {
      setAccordionState([false, false, false, false, false]);
      setCanExpandAll(true);
    } else {
      setAccordionState([true, true, true, true, true]);
      setCanExpandAll(false);
    }
  };

  const onAccordionToggleClicked = (id: number) => {
    const isOpening = !accordionState[id];
    let state;
    if (isOpening) {
      //collapse all panel
      state = [false, false, false, false, false];
      state[id] = true;
    } else {
      state = [...accordionState];
      state[id] = false;
    }
    //check if no panel expanded
    if (state.every((value) => value === false)) {
      setCanExpandAll(true);
    }
    if (state.every((value) => value === true)) {
      setCanExpandAll(false);
    }
    setAccordionState(state);
  };

  let plate = imagerState.plate
    ? imagerState.plate
    : {
        experimentName: "No plate",
        projectName: "No plate",
        plateType: {
          plateTypeName: "No plate",
          numberOfDropPerWell: 1,
        },
        barcode: "No plate",
        plateId: "No plate",
        userName: "No plate",
      };

  const originPlateAddress = imagerState.plate
    ? PlateUtil.getReservedPlateAddress(imagerState.plate, plateAddresses)
    : null;

  const originModule = originPlateAddress
    ? `${moduleById[originPlateAddress.moduleIdentifier].name} Hotel ${
        originPlateAddress.hotelNo
      }, Slot ${originPlateAddress.slotNo}`
    : "No plate";

  let dropDisplayValues = [];
  for (let i = 1; i <= plate.plateType.numberOfDropPerWell; i++) {
    dropDisplayValues.push({
      display: i.toString(),
      value: i,
      disabled: false,
    });
  }

  return (
    <div
      style={{
        backgroundColor: "white",
        height: "100%",
        overflow: "auto",
        ...style,
      }}
    >
      {headerEnabled && (
        <AccordionToggle
          doubleChevron
          expanded={!canExpandAll}
          onClick={() => onExpandAllToggleClicked()}
        >
          <div>Control Panel</div>
        </AccordionToggle>
      )}
      <AccordionToggle
        expanded={accordionState[WELL]}
        onClick={() => onAccordionToggleClicked(WELL)}
      >
        <div
          style={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          <div>Well Control</div>
          {/* --------DROP SELECTION-------- */}
          <div
            style={{
              flexGrow: 1,
              textAlign: "right",
              marginRight: "10px",
            }}
          >
            Drop
          </div>
          <CommonDropDown
            id="drop-plate-dropdown"
            style={{
              width: "78px",
              height: "35px",
            }}
            onChange={(value) => onDropNumberChanged(value)}
            value={imagerState.dropNumber}
            disabled={disableWellControl}
            dropDownDisplayValues={dropDisplayValues}
          />
        </div>
      </AccordionToggle>
      <Collapse expanded={accordionState[WELL]}>
        <Disabled disabled={disableWellControl}>
          <WellControl
            key="WellControl"
            imagerIdentifier={imagerIdentifier}
            disabled={disableWellControl}
            wellLimitEnabled={wellLimitEnabled}
          />
        </Disabled>
      </Collapse>

      {/*--------OPTIC AND CAMERA CONTROL--------*/}
      <AccordionToggle
        expanded={accordionState[OPTIC]}
        onClick={() => onAccordionToggleClicked(OPTIC)}
      >
        Optics and Camera Control
      </AccordionToggle>
      <Collapse expanded={accordionState[OPTIC]}>
        <Disabled disabled={disableOpticControl}>
          <OpticAndCameraControl
            disabled={disableOpticControl}
            additionalControlDisabled={disableAdditionalOpticControl}
            imagerIdentifier={imager.identifier}
            disabledLightPaths={disabledLightPaths}
          />
        </Disabled>
      </Collapse>

      {/*--------LEVELING--------*/}
      <AccordionToggle
        expanded={accordionState[LEVELING]}
        onClick={() => onAccordionToggleClicked(LEVELING)}
      >
        Leveling
      </AccordionToggle>
      <Collapse expanded={accordionState[LEVELING]}>
        <Disabled disabled={!plateExistOnImager}>
          <ImageLeveling imagerIdentifier={imagerIdentifier} />
        </Disabled>
      </Collapse>
      {/*-------PREFERENCES---------*/}
      <AccordionToggle
        expanded={accordionState[PREFERENCES]}
        onClick={() => onAccordionToggleClicked(PREFERENCES)}
      >
        Live Image Options
      </AccordionToggle>
      <Collapse expanded={accordionState[PREFERENCES]}>
        <Disabled disabled={!plateExistOnImager}>
          <LiveImagePreferences imagerIdentifier={imagerIdentifier} />
        </Disabled>
      </Collapse>
      {/*--------PLATE INFO--------*/}
      <AccordionToggle
        expanded={accordionState[INFO]}
        onClick={() => onAccordionToggleClicked(INFO)}
      >
        Plate Information
      </AccordionToggle>
      <Collapse expanded={accordionState[INFO]}>
        <div>
          <div
            style={{
              width: "calc(100% - 20px)",
              minHeight: 250,
              textAlign: "left",
              border: "1px solid #E0E0E0",
              margin: "10px",
              padding: "10px",
              fontFamily: "Roboto",
              fontSize: 12,
            }}
          >
            <table
              style={{
                textAlign: "left",
                borderCollapse: "separate",
                borderSpacing: "2px 17px",
                marginLeft: "40px",
              }}
            >
              <tbody>
                <tr>
                  <th style={{ textAlign: "right" }}>Project:</th>
                  <td>{plate.projectName}</td>
                </tr>

                <tr>
                  <th style={{ textAlign: "right" }}>Experiment:</th>
                  <td>{plate.experimentName}</td>
                </tr>

                <tr>
                  <th style={{ textAlign: "right" }}>Plate Type:</th>
                  <td>{plate.plateType.plateTypeName}</td>
                </tr>

                <tr>
                  <th style={{ textAlign: "right" }}>Location:</th>
                  <td>{originModule}</td>
                </tr>

                <tr>
                  <th style={{ textAlign: "right" }}>Barcode:</th>
                  <td>{plate.barcode}</td>
                </tr>

                <tr>
                  <th style={{ textAlign: "right" }}>Plate Id:</th>
                  <td>{plate.plateId}</td>
                </tr>

                <tr>
                  <th style={{ textAlign: "right" }}>User Name:</th>
                  <td>{plate.userName}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </Collapse>
    </div>
  );
}
